import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Coffee, RefreshCw, Search, RotateCcw, Users, Activity } from 'lucide-react';
import { BreakPagination } from '../hr/break/ui/BreakUi';
import { TeamStatusTable, BreakHistoryTable } from './ITBreakTables';
import { AcknowledgeBreakModal } from '../hr/break/AcknowledgeBreakModal';
import { formatTimeOnlyET } from '../../utils/formatters';

/* IT-BREAK-MGMT-001 — IT Manager Break Management tab (Team Management).
   Monitoring only: reuses GET /api/breaks/scope-users, /team-breaks and /history (team-scoped server-side). */
const API = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_SIZE = 20;
const POLL_MS = 30_000;
const dayStr = (offset = 0) => { const d = new Date(); d.setDate(d.getDate() + offset); return d.toISOString().split('T')[0]; };
const HISTORY_STATUS = [['', 'All statuses'], ['active', 'Active (in progress)'], ['completed', 'Completed'], ['auto_ended', 'Auto-Ended'], ['force_ended', 'Force-Ended']];
const ctl = 'h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-[12.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:border-blue-400 transition-[border-color,box-shadow]';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const Stat = ({ icon: Icon, label, value, tone, testId }) => (
  <div className={`bm-rise flex items-center gap-3 rounded-xl border px-4 py-3 ${tone}`} data-testid={testId}>
    <span className="h-9 w-9 rounded-lg bg-white/70 flex items-center justify-center"><Icon className="w-4 h-4" /></span>
    <div><div className="text-xl font-bold leading-none tabular-nums" data-testid={`${testId}-value`}>{value}</div><div className="text-[11px] font-medium mt-0.5 opacity-80">{label}</div></div>
  </div>
);

const ITBreakManagement = ({ currentUser }) => {
  const [members, setMembers] = useState([]);
  const [active, setActive] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [search, setSearch] = useState('');
  const [statusView, setStatusView] = useState('all');
  const [history, setHistory] = useState({ breaks: [], total: 0, pages: 1 });
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [page, setPage] = useState(1);
  const [hUser, setHUser] = useState('');
  const [dateFrom, setDateFrom] = useState(dayStr(-7));
  const [dateTo, setDateTo] = useState(dayStr(0));
  const [hStatus, setHStatus] = useState('');
  const [ackTarget, setAckTarget] = useState(null); // IT-BREAK-ACK-001
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async (poll = false) => {
    poll ? setRefreshing(true) : setLoadingStatus(true);
    try {
      const [u, a] = await Promise.all([axios.get(`${API}/api/breaks/scope-users`, auth()), axios.get(`${API}/api/breaks/team-breaks`, auth())]);
      setMembers((u.data.users || []).filter((m) => m.id !== currentUser?.id));
      setActive(a.data.active_breaks || []);
      setLastRefresh(new Date()); setError(null);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setLoadingStatus(false); setRefreshing(false); }
  }, [currentUser?.id]);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const p = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (hUser) p.append('user_id', hUser);
      if (dateFrom) p.append('date_from', dateFrom);
      if (dateTo) p.append('date_to', dateTo);
      if (hStatus) p.append('status', hStatus);
      const r = await axios.get(`${API}/api/breaks/history?${p}`, auth());
      setHistory({ breaks: r.data.breaks || [], total: r.data.total || 0, pages: r.data.pages || 1 });
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setLoadingHistory(false); }
  }, [page, hUser, dateFrom, dateTo, hStatus]);

  useEffect(() => { fetchStatus(false); const t = setInterval(() => fetchStatus(true), POLL_MS); return () => clearInterval(t); }, [fetchStatus]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const rows = useMemo(() => {
    const byUser = Object.fromEntries(active.map((b) => [b.user_id, b]));
    const q = search.trim().toLowerCase();
    return members.map((m) => ({ id: m.id, name: m.full_name || m.email, activeBreak: byUser[m.id] || null }))
      .filter((r) => !q || r.name.toLowerCase().includes(q))
      .filter((r) => statusView === 'all' || (statusView === 'on_break' ? !!r.activeBreak : !r.activeBreak))
      .sort((a, b) => (b.activeBreak ? 1 : 0) - (a.activeBreak ? 1 : 0) || a.name.localeCompare(b.name));
  }, [members, active, search, statusView]);
  const onBreakCount = members.filter((m) => active.some((b) => b.user_id === m.id)).length;
  const resetHistory = () => { setHUser(''); setDateFrom(dayStr(-7)); setDateTo(dayStr(0)); setHStatus(''); setPage(1); };

  return (
    <div className="space-y-5" data-testid="it-break-management">
      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-[12.5px] text-rose-700" role="alert" data-testid="it-break-error">{String(error)}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat icon={Users} label="Team members" value={members.length} tone="border-slate-200 bg-slate-50 text-slate-800" testId="it-break-stat-team" />
        <Stat icon={Activity} label="Active (working)" value={Math.max(0, members.length - onBreakCount)} tone="border-emerald-200 bg-emerald-50 text-emerald-700" testId="it-break-stat-active" />
        <Stat icon={Coffee} label="On break now" value={onBreakCount} tone="border-amber-200 bg-amber-50 text-amber-700" testId="it-break-stat-on-break" />
      </div>

      <section className="space-y-3" data-testid="it-break-status-section">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-slate-900">Team Status</h3>
            <p className="text-[12px] text-slate-500">Live break status of your team · auto-refreshes every 30 seconds</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee…" className={`${ctl} pl-8 w-52`} data-testid="it-break-search" /></div>
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="tablist" data-testid="it-break-status-view">
              {[['all', 'All'], ['active', 'Active'], ['on_break', 'On Break']].map(([k, l]) => (
                <button key={k} type="button" role="tab" aria-selected={statusView === k} onClick={() => setStatusView(k)} data-testid={`it-break-status-view-${k}`}
                  className={`h-8 px-3 rounded-md text-[12px] font-semibold transition-[background-color,color] ${statusView === k ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}>{l}</button>
              ))}
            </div>
            <button type="button" onClick={() => fetchStatus(true)} disabled={refreshing} className="bm-btn h-9 px-3 rounded-lg border border-slate-200 bg-white text-[12.5px] font-medium text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="it-break-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />Refresh
            </button>
          </div>
        </div>
        <TeamStatusTable rows={rows} loading={loadingStatus} />
        {lastRefresh && <p className="text-[11px] text-slate-400 inline-flex items-center gap-1.5" data-testid="it-break-last-refresh"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />Last updated {formatTimeOnlyET(lastRefresh.toISOString())}</p>}
      </section>

      <section className="space-y-3" data-testid="it-break-history-section">
        <div>
          <h3 className="text-base font-bold text-slate-900">Break History</h3>
          <p className="text-[12px] text-slate-500">Recorded breaks for your team members</p>
        </div>
        <div className="bm-rise grid grid-cols-2 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-end rounded-2xl border border-slate-200 bg-white px-4 py-3" data-testid="it-break-history-filters">
          <label className="block text-[10.5px] font-medium text-slate-500">Employee
            <select value={hUser} onChange={(e) => { setHUser(e.target.value); setPage(1); }} className={`${ctl} mt-1`} data-testid="it-break-filter-user">
              <option value="">All team members ({members.length})</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.full_name || m.email}</option>)}
            </select>
          </label>
          <label className="block text-[10.5px] font-medium text-slate-500">From<input type="date" value={dateFrom} max={dateTo} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className={`${ctl} mt-1`} data-testid="it-break-filter-from" /></label>
          <label className="block text-[10.5px] font-medium text-slate-500">To<input type="date" value={dateTo} min={dateFrom} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className={`${ctl} mt-1`} data-testid="it-break-filter-to" /></label>
          <label className="block text-[10.5px] font-medium text-slate-500">Status
            <select value={hStatus} onChange={(e) => { setHStatus(e.target.value); setPage(1); }} className={`${ctl} mt-1`} data-testid="it-break-filter-status">
              {HISTORY_STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <button type="button" onClick={resetHistory} className="bm-btn h-9 px-3 rounded-lg text-[12.5px] font-medium text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1.5" data-testid="it-break-filter-reset"><RotateCcw className="w-3.5 h-3.5" />Reset</button>
        </div>
        <BreakHistoryTable breaks={history.breaks} loading={loadingHistory} onAcknowledge={setAckTarget} />
        {ackTarget && (
          <AcknowledgeBreakModal open onOpenChange={(v) => { if (!v) setAckTarget(null); }} breakRecord={ackTarget}
            onSuccess={(updated) => setHistory((h) => ({ ...h, breaks: h.breaks.map((b) => (b.id === updated.id ? { ...b, ...updated } : b)) }))} />
        )}
        {history.total > 0 && (
          <BreakPagination page={page} pages={history.pages} shown={history.breaks.length} total={history.total} noun={`break${history.total === 1 ? '' : 's'}`}
            onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(history.pages, p + 1))} testId="it-break-history-pagination" />
        )}
      </section>
    </div>
  );
};

export default ITBreakManagement;
